import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import './frontend.css';

const TOTAL_STEPS = 4;

const ArrowIcon = () => (
	<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M4.167 10H15.833M15.833 10L10.833 5M15.833 10L10.833 15"
			stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const BackArrowIcon = () => (
	<svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M15.833 10H4.167M4.167 10L9.167 5M4.167 10L9.167 15"
			stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

function OptionCard({ option, isSelected, isDisabled, onSelect }) {
	const classes = [
		'cfg-option-card',
		isSelected ? 'cfg-option-card--selected' : '',
		isDisabled ? 'cfg-option-card--disabled' : '',
	].filter(Boolean).join(' ');

	return (
		<button
			className={classes}
			onClick={() => !isDisabled && onSelect(option)}
			disabled={isDisabled}
			type="button"
		>
			{option.image && (
				<div className="cfg-option-card__image">
					<img src={option.image} alt={option.title} loading="lazy" />
				</div>
			)}
			<span className="cfg-option-card__title">{option.title}</span>
		</button>
	);
}

function ProgressIndicator({ current, total }) {
	return (
		<div className="cfg-progress">
			<span className="cfg-progress__current">{current}</span>
			<span className="cfg-progress__separator">/</span>
			<span className="cfg-progress__total">{total}</span>
		</div>
	);
}

function StepView({ level, selectedOptions, onSelect, direction }) {
	const animClass = direction === 'forward'
		? 'cfg-step--enter-right'
		: 'cfg-step--enter-left';

	const selectedIds = new Set((selectedOptions || []).map(o => o.id));

	return (
		<div className={`cfg-step ${animClass}`} key={level.key}>
			<h2 className="cfg-step__title">{level.label}</h2>
			<div className="cfg-options-grid">
				{level.filteredOptions.map(opt => (
					<OptionCard
						key={opt.id}
						option={opt}
						isSelected={selectedIds.has(opt.id)}
						isDisabled={opt._disabled}
						onSelect={onSelect}
					/>
				))}
			</div>
		</div>
	);
}

function ContactStep({ selections, levels, formContainerRef, contactTitle, contactDescription }) {
	const summaryItems = levels.map((level, i) => {
		const sel = selections[i];
		return sel?.length ? { label: level.label, value: sel.map(o => o.title).join(', ') } : null;
	}).filter(Boolean);

	return (
		<div className="cfg-step cfg-step--enter-right cfg-contact-step">
			{contactTitle && (
				<h2 className="cfg-step__title">{contactTitle}</h2>
			)}
			{contactDescription && (
				<p className="cfg-contact-step__description">{contactDescription}</p>
			)}
			{false && summaryItems.length > 0 && (
				<div className="cfg-summary">
					{summaryItems.map((item, i) => (
						<div key={i} className="cfg-summary__item">
							<span className="cfg-summary__label">{item.label}</span>
							<span className="cfg-summary__value">{item.value}</span>
						</div>
					))}
				</div>
			)}
			<div className="cfg-contact-form" ref={formContainerRef}></div>
		</div>
	);
}

function Configurator({ config, contactFormEl }) {
	const [currentStep, setCurrentStep] = useState(0);
	const [selections, setSelections] = useState({});
	const [direction, setDirection] = useState('forward');
	const formContainerRef = useRef(null);
	const isContactStep = currentStep === TOTAL_STEPS;

	const getFilteredOptions = useCallback((levelIndex) => {
		const level = config.levels[levelIndex];
		if (!level) return [];

		if (levelIndex === 0) {
			return level.options.map(opt => ({ ...opt, _disabled: false }));
		}

		const parentSelections = selections[levelIndex - 1] || [];
		if (parentSelections.length === 0) {
			return level.options.map(opt => ({ ...opt, _disabled: false }));
		}

		// If ANY selected parent has empty nextOptions it means "all options allowed".
		// The union of a bounded set with an unbounded set is unbounded — show everything.
		const anyShowsAll = parentSelections.some(
			sel => !sel.nextOptions || sel.nextOptions.length === 0
		);

		if (anyShowsAll) {
			return level.options.map(opt => ({ ...opt, _disabled: false }));
		}

		// All selected parents have explicit filters — show the union of their nextOptions.
		const validIds = new Set(parentSelections.flatMap(sel => sel.nextOptions));

		return level.options.map(opt => ({
			...opt,
			_disabled: !validIds.has(opt.id),
		}));
	}, [config.levels, selections]);

	const levelsWithFiltered = config.levels.map((level, i) => ({
		...level,
		filteredOptions: getFilteredOptions(i),
	}));

	const handleSelect = (option) => {
		setSelections(prev => {
			const current = prev[currentStep] || [];
			const alreadySelected = current.some(o => o.id === option.id);
			const updated = alreadySelected
				? current.filter(o => o.id !== option.id)
				: [...current, option];
			return { ...prev, [currentStep]: updated };
		});
	};

	const handleNext = () => {
		if (!selections[currentStep]?.length) return;
		setDirection('forward');
		if (currentStep < TOTAL_STEPS) {
			setCurrentStep(currentStep + 1);
		}
	};

	const handlePrevious = () => {
		if (currentStep > 0) {
			setDirection('backward');
			setCurrentStep(currentStep - 1);
		}
	};

	const handleHelpMeDecide = () => {
		setDirection('forward');
		setCurrentStep(TOTAL_STEPS);
	};

	useEffect(() => {
		if (isContactStep && formContainerRef.current && contactFormEl) {
			formContainerRef.current.innerHTML = '';
			formContainerRef.current.appendChild(contactFormEl);
			contactFormEl.style.display = '';

			// Re-initialize CF7 AJAX after the form element has been moved in the DOM,
			// otherwise CF7 falls back to standard POST and reloads the page.
			const cf7Form = contactFormEl.querySelector('.wpcf7-form');
			if (cf7Form && window.wpcf7?.init) {
				window.wpcf7.init(cf7Form);
			}

			const summaryParts = [];
			config.levels.forEach((level, i) => {
				const sel = selections[i];
				if (sel?.length) {
					summaryParts.push(`${level.label}: ${sel.map(o => o.title).join(', ')}`);
				}
			});
			const summaryText = summaryParts.join('\n');

			const fillMessageField = () => {
				const messageField =
					contactFormEl.querySelector('textarea[name="your-message"]') ||
					contactFormEl.querySelector('input[name="your-message"]') ||
					Array.from(contactFormEl.querySelectorAll('textarea, input[type="text"]')).find(el =>
						el.name && (el.name.includes('message') || el.name.includes('project') || el.name.includes('details'))
					);

				if (messageField) {
					messageField.value        = summaryText;
					messageField.defaultValue = summaryText;
					messageField.dispatchEvent(new Event('input',  { bubbles: true }));
					messageField.dispatchEvent(new Event('change', { bubbles: true }));
				}
			};

			// Fill immediately, then once more after CF7 finishes reinitialising
			fillMessageField();
			setTimeout(fillMessageField, 150);
		}
	}, [isContactStep, contactFormEl, selections, config.levels]);

	const currentSelections = selections[currentStep] || [];
	const isLastLevel = currentStep === TOTAL_STEPS - 1;

	return (
		<div className="cfg-wizard">
			<div className="cfg-header">
				<div className="cfg-header__left">
					{currentStep > 0 && (
						<button className="cfg-back-btn" onClick={handlePrevious} type="button">
							<BackArrowIcon />
							<span>Previous</span>
						</button>
					)}
				</div>
				<div className="cfg-header__right">
					{!isContactStep && (
						<ProgressIndicator current={currentStep + 1} total={TOTAL_STEPS} />
					)}
				</div>
			</div>

			<div className="cfg-content">
				{!isContactStep ? (
					<StepView
						level={levelsWithFiltered[currentStep]}
						selectedOptions={currentSelections}
						onSelect={handleSelect}
						direction={direction}
					/>
				) : (
					<ContactStep
						selections={selections}
						levels={config.levels}
						formContainerRef={formContainerRef}
						contactTitle={config.contactTitle}
						contactDescription={config.contactDescription}
					/>
				)}
			</div>

			{!isContactStep && (
				<div className="cfg-footer">
										{currentStep > 0 && (
						<button
							className="cfg-help-btn"
							onClick={handleHelpMeDecide}
							type="button"
						>
							Help me decide
						</button>
					)}
					<button
						className="cfg-next-btn"
						onClick={handleNext}
						disabled={!currentSelections.length}
						type="button"
					>
						<span>{isLastLevel ? 'Done' : 'Next'}</span>
						<ArrowIcon />
					</button>


				</div>
			)}
		</div>
	);
}

function initConfigurator() {
	const containers = document.querySelectorAll('.configurator-container');

	containers.forEach(container => {
		if (container.dataset.reactInitialized === 'true') return;

		const configRaw = container.dataset.configuratorConfig;
		if (!configRaw) return;

		let config;
		try {
			config = JSON.parse(configRaw);
		} catch (e) {
			console.error('Configurator: failed to parse config JSON', e);
			return;
		}

		const contactFormEl = container.querySelector('#configurator-contact-form');
		if (contactFormEl) {
			contactFormEl.style.display = 'none';
			contactFormEl.parentNode.removeChild(contactFormEl);
		}

		const appEl = container.querySelector('#configurator-app');
		if (!appEl) return;

		const root = createRoot(appEl);
		root.render(
			<Configurator config={config} contactFormEl={contactFormEl} />
		);

		container.dataset.reactInitialized = 'true';
	});
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initConfigurator);
} else {
	initConfigurator();
}
